import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Wind, Clock, TrendingUp, Trash2, StickyNote, Check, X as XIcon, Pencil, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/utils/constants';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

// Custom tooltip component for charts
const CustomTooltip = ({ active, payload, color, unit = 's' }) => {
  if (active && payload && payload.length) {
    const v = payload[0].value || 0;
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
        <p className="text-xs text-slate-500">{payload[0].payload.fullDate}</p>
        <p className="text-lg font-bold" style={{ color }}>
          {unit === 'pas' ? `${v} pas` : `${v.toFixed(1)}s`}
        </p>
      </div>
    );
  }
  return null;
};

const HistoryPage = () => {
  const [isMounted, setIsMounted] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState({ total_sessions: 0, total_minutes: 0, total_cycles: 0 });
  const [challengeScores, setChallengeScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('records');
  // Which challenge type has its "all measures" list expanded?
  const [expandedListType, setExpandedListType] = useState(null);
  
  const [records, setRecords] = useState({
    inhale: { time: 0, date: null },
    exhale: { time: 0, date: null },
    hold: { time: 0, date: null },
    apnea: { time: 0, date: null },
    mbt: { steps: 0, date: null }
  });
  // Authoritative best-per-type snapshot returned by GET /challenges/records.
  // Used to hydrate `records` so we never miss a record older than the last
  // 50 challenges (see fetchData comment for details).
  const [serverRecords, setServerRecords] = useState(null);

  // Inline note edition for challenge scores
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);

  const startEditNote = (score) => {
    setEditingNoteId(score.id);
    setNoteDraft(score.notes || '');
  };
  const cancelEditNote = () => {
    setEditingNoteId(null);
    setNoteDraft('');
  };
  const saveNote = async (score) => {
    setNoteSaving(true);
    try {
      const resp = await axios.patch(`${API}/challenges/${score.id}`, { notes: noteDraft });
      const newNotes = resp?.data?.notes || null;
      setChallengeScores((prev) => prev.map((s) => s.id === score.id ? { ...s, notes: newNotes } : s));
      toast.success('Note enregistrée');
      cancelEditNote();
    } catch (err) {
      console.error(err);
      toast.error("Impossible d'enregistrer la note");
    } finally {
      setNoteSaving(false);
    }
  };

  // Mark component as mounted for client-side only rendering (fixes recharts in production)
  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  // Update records when challengeScores are loaded.
  // Priority: use `serverRecords` (authoritative /challenges/records payload,
  // same as MonEspacePage) — otherwise fall back to a client-side scan.
  useEffect(() => {
    if (serverRecords) {
      setRecords({
        inhale: {
          time: serverRecords.inhale?.duration_seconds || 0,
          date: serverRecords.inhale?.completed_at || null,
        },
        exhale: {
          time: serverRecords.exhale?.duration_seconds || 0,
          date: serverRecords.exhale?.completed_at || null,
        },
        hold: {
          time: serverRecords.hold?.duration_seconds || 0,
          date: serverRecords.hold?.completed_at || null,
        },
        apnea: {
          time: serverRecords.apnea?.duration_seconds || 0,
          date: serverRecords.apnea?.completed_at || null,
        },
        mbt: {
          steps: serverRecords.mbt?.steps || 0,
          date: serverRecords.mbt?.completed_at || null,
        },
      });
      return;
    }
    if (challengeScores.length > 0) {
      const inhaleRecords = challengeScores.filter(s => s.challenge_type === 'inhale');
      const exhaleRecords = challengeScores.filter(s => s.challenge_type === 'exhale');
      const holdRecords = challengeScores.filter(s => s.challenge_type === 'hold');
      const apneaRecords = challengeScores.filter(s => s.challenge_type === 'apnea');
      const mbtRecords = challengeScores.filter(s => s.challenge_type === 'mbt');

      const bestInhale = inhaleRecords.length > 0 
        ? inhaleRecords.reduce((max, s) => s.duration_seconds > max.duration_seconds ? s : max)
        : null;
      const bestExhale = exhaleRecords.length > 0 
        ? exhaleRecords.reduce((max, s) => s.duration_seconds > max.duration_seconds ? s : max)
        : null;
      const bestHold = holdRecords.length > 0 
        ? holdRecords.reduce((max, s) => s.duration_seconds > max.duration_seconds ? s : max)
        : null;
      const bestApnea = apneaRecords.length > 0 
        ? apneaRecords.reduce((max, s) => s.duration_seconds > max.duration_seconds ? s : max)
        : null;
      const bestMbt = mbtRecords.length > 0 
        ? mbtRecords.reduce((max, s) => (s.steps || 0) > (max.steps || 0) ? s : max)
        : null;

      setRecords({
        inhale: { 
          time: bestInhale ? bestInhale.duration_seconds : 0,
          date: bestInhale ? bestInhale.completed_at : null
        },
        exhale: { 
          time: bestExhale ? bestExhale.duration_seconds : 0,
          date: bestExhale ? bestExhale.completed_at : null
        },
        hold: { 
          time: bestHold ? bestHold.duration_seconds : 0,
          date: bestHold ? bestHold.completed_at : null
        },
        apnea: { 
          time: bestApnea ? bestApnea.duration_seconds : 0,
          date: bestApnea ? bestApnea.completed_at : null
        },
        mbt: { 
          steps: bestMbt ? (bestMbt.steps || 0) : 0,
          date: bestMbt ? bestMbt.completed_at : null
        }
      });
    }
  }, [challengeScores, serverRecords]);

  // Prepare chart data for each challenge type
  const chartData = useMemo(() => {
    if (!challengeScores || challengeScores.length === 0) {
      return {
        inhale: [],
        exhale: [],
        hold: [],
        mbt: []
      };
    }

    const prepareData = (type) => {
      try {
        return challengeScores
          .filter(s => s && s.challenge_type === type)
          .sort((a, b) => new Date(a.completed_at) - new Date(b.completed_at))
          .map(s => {
            const d = new Date(s.completed_at);
            return {
              xKey: s.id || s.completed_at, // unique per session
              date: d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
              fullDate: d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) +
                        ' à ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
              score: type === 'mbt' ? (Number(s.steps) || 0) : (Number(s.duration_seconds) || 0),
              isRecord: s.is_record
            };
          });
      } catch {
        return [];
      }
    };

    return {
      inhale: prepareData('inhale'),
      exhale: prepareData('exhale'),
      hold: prepareData('hold'),
      apnea: prepareData('apnea'),
      mbt: prepareData('mbt')
    };
  }, [challengeScores]);

  const fetchData = async () => {
    try {
      // NOTE: /challenges has a default `limit=50` on the backend. If a user
      // has more than 50 recent scores across all types, older MBT / apnée
      // records can get truncated → they then disappear from the "Records"
      // tab even though they still exist in the DB. We now fetch a large
      // window (1000) AND read the authoritative per-type best from the
      // dedicated /challenges/records endpoint (same source used by
      // MonEspacePage), so both pages always agree on the numbers.
      const [sessionsRes, statsRes, challengesRes, recordsRes] = await Promise.all([
        axios.get(`${API}/sessions`),
        axios.get(`${API}/sessions/stats`),
        axios.get(`${API}/challenges`, { params: { limit: 1000 } }),
        axios.get(`${API}/challenges/records`),
      ]);
      setSessions(sessionsRes.data);
      setStats(statsRes.data);
      setChallengeScores(challengesRes.data);
      setServerRecords(recordsRes.data || null);
    } catch {
      toast.error("Échec du chargement de l'historique");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const formatRecordDate = (dateStr) => {
    if (!dateStr) return 'Pas encore de record';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getChallengeLabel = (type) => {
    switch (type) {
      case 'inhale': return 'Inspiration';
      case 'exhale': return 'Expiration';
      case 'hold': return 'Apnée (BOLT)';
      case 'apnea': return 'Apnée';
      case 'mbt': return 'MBT';
      default: return type;
    }
  };

  const getChallengeColor = (type) => {
    switch (type) {
      case 'inhale': return { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-600', hex: '#d97706' };
      case 'exhale': return { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-600', hex: '#0284c7' };
      case 'hold': return { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-600', hex: '#7c3aed' };
      case 'apnea': return { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-600', hex: '#059669' };
      case 'mbt': return { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-600', hex: '#e11d48' };
      default: return { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-600', hex: '#6b7280' };
    }
  };

  // Render a challenge chart
  const renderChallengeChart = (type, title, gradientFrom, gradientTo) => {
    const data = chartData[type] || [];
    const colors = getChallengeColor(type);
    const isMbt = type === 'mbt';
    const record = isMbt ? (records.mbt?.steps || 0) : (records[type]?.time || 0);
    const unit = isMbt ? 'pas' : 's';
    const recordDisplay = isMbt ? `${record} pas` : `${(record || 0).toFixed(1)}s`;

    if (!data || data.length === 0) {
      return (
        <Card className={`${colors.bg} ${colors.border} mb-4`} key={type}>
          <CardContent className="py-4">
            <h3 className={`font-semibold ${colors.text} mb-2`}>{title}</h3>
            <p className="text-sm text-slate-500 text-center py-4">Aucune donnée disponible</p>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className={`bg-gradient-to-br ${gradientFrom} ${gradientTo} border-0 shadow-lg mb-4`} key={type}>
        <CardContent className="py-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-slate-800">{title}</h3>
            <div className="text-right">
              <span className="text-xs text-slate-500">Record: </span>
              <span className={`font-bold ${colors.text}`}>{recordDisplay}</span>
            </div>
          </div>
          <div className="h-48 w-full" style={{ minHeight: '192px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="xKey" 
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  tickLine={false}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickFormatter={(value) => {
                    const point = data.find(d => d.xKey === value);
                    return point ? point.date : '';
                  }}
                />
                <YAxis 
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  tickLine={false}
                  axisLine={{ stroke: '#e2e8f0' }}
                  unit={unit === 'pas' ? '' : 's'}
                  domain={[0, 'auto']}
                />
                <Tooltip content={<CustomTooltip color={colors.hex} unit={unit} />} />
                {record > 0 && (
                  <ReferenceLine 
                    y={record} 
                    stroke={colors.hex} 
                    strokeDasharray="5 5" 
                    strokeWidth={2}
                    label={{ value: 'Record', position: 'right', fontSize: 10, fill: colors.hex }}
                  />
                )}
                <Line 
                  type="monotone" 
                  dataKey="score" 
                  stroke={colors.hex}
                  strokeWidth={3}
                  dot={{ fill: colors.hex, strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, fill: colors.hex }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="text-xs text-slate-500 text-center mt-2">
            {data.length} entrée{data.length > 1 ? 's' : ''} enregistrée{data.length > 1 ? 's' : ''}
          </p>

          {/* Expandable list of every recorded value with a delete button.
              Lets the patient remove buggy / mistaken entries. */}
          <button
            type="button"
            onClick={() => setExpandedListType(expandedListType === type ? null : type)}
            className="w-full mt-3 flex items-center justify-center gap-1.5 text-xs font-medium text-slate-600 hover:text-slate-900 py-1.5 rounded-lg hover:bg-white/50 transition"
            data-testid={`toggle-list-${type}`}
          >
            {expandedListType === type ? (
              <>
                <ChevronUp size={14} /> Masquer les mesures
              </>
            ) : (
              <>
                <ChevronDown size={14} /> Voir toutes les mesures ({data.length})
              </>
            )}
          </button>

          {expandedListType === type && (
            <div
              className="mt-2 bg-white/70 rounded-lg divide-y divide-slate-200 max-h-72 overflow-y-auto"
              data-testid={`measures-list-${type}`}
            >
              {[...challengeScores]
                .filter((s) => s.challenge_type === type)
                .sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at))
                .map((score) => {
                  const value = unit === 'pas' ? `${score.steps || score.duration_seconds} pas` : `${(score.duration_seconds || 0).toFixed(1)} s`;
                  return (
                    <div
                      key={score.id}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-white transition"
                      data-testid={`measure-row-${score.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold" style={{ color: colors.hex }}>{value}</p>
                        <p className="text-[11px] text-slate-500">{formatDate(score.completed_at)}</p>
                        {score.notes && (
                          <p className="text-[11px] text-slate-600 italic truncate mt-0.5">"{score.notes}"</p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeleteChallenge(score)}
                        className="text-slate-400 hover:text-red-600 p-1.5 rounded-md hover:bg-red-50 transition flex-shrink-0"
                        title="Supprimer cette mesure"
                        data-testid={`delete-measure-${score.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  );
                })}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const handleDeleteChallenge = async (score) => {
    const label = getChallengeLabel(score.challenge_type);
    const value = (score.duration_seconds || 0).toFixed(1);
    if (!window.confirm(`Supprimer cette mesure ${label} (${value} s du ${formatDate(score.completed_at)}) ?`)) return;
    try {
      await axios.delete(`${API}/challenges/${score.id}`);
      setChallengeScores((arr) => arr.filter((s) => s.id !== score.id));
      toast.success('Mesure supprimée');
    } catch {
      toast.error('Suppression impossible');
    }
  };

  return (
    <div 
      className="min-h-screen pb-40 sm:pb-24" 
      data-testid="history-page"
      style={{
        backgroundImage: 'url(/images/history-patterns-bg.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="max-w-md mx-auto px-6 pt-8">
        <h1 className="text-2xl font-bold text-stone-800 mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Votre Parcours
        </h1>

        <div className="grid grid-cols-3 gap-3 mb-6">
          <Card className="bg-white/70 backdrop-blur border-stone-200">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-teal-600" data-testid="stat-sessions">{stats.total_sessions}</p>
              <p className="text-xs text-stone-500">Sessions</p>
            </CardContent>
          </Card>
          <Card className="bg-white/70 backdrop-blur border-stone-200">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-teal-600" data-testid="stat-minutes">{stats.total_minutes}</p>
              <p className="text-xs text-stone-500">Minutes</p>
            </CardContent>
          </Card>
          <Card className="bg-white/70 backdrop-blur border-stone-200">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-teal-600" data-testid="stat-cycles">{stats.total_cycles}</p>
              <p className="text-xs text-stone-500">Cycles</p>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full bg-white/70 backdrop-blur mb-4 flex-wrap h-auto">
            <TabsTrigger value="records" className="flex-1 text-xs sm:text-sm" data-testid="tab-records">Records</TabsTrigger>
            <TabsTrigger value="progression" className="flex-1 text-xs sm:text-sm" data-testid="tab-progression">Progression</TabsTrigger>
            <TabsTrigger value="challenges" className="flex-1 text-xs sm:text-sm" data-testid="tab-challenges">Défis</TabsTrigger>
            <TabsTrigger value="wimhof" className="flex-1 text-xs sm:text-sm" data-testid="tab-wimhof">Wim Hof</TabsTrigger>
            <TabsTrigger value="sessions" className="flex-1 text-xs sm:text-sm" data-testid="tab-sessions">Sessions</TabsTrigger>
          </TabsList>

          <TabsContent value="records">
            <div className="space-y-3">
              <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
                <CardContent className="py-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-amber-800">Inspiration Max</p>
                      <p className="text-xs text-amber-600 mt-1">{formatRecordDate(records.inhale?.date)}</p>
                    </div>
                    <p className="text-2xl font-bold text-amber-600" data-testid="record-inhale">
                      {(records.inhale?.time || 0).toFixed(1)}s
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-r from-sky-50 to-indigo-50 border-sky-200">
                <CardContent className="py-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-sky-800">Expiration Max</p>
                      <p className="text-xs text-sky-600 mt-1">{formatRecordDate(records.exhale?.date)}</p>
                    </div>
                    <p className="text-2xl font-bold text-sky-600" data-testid="record-exhale">
                      {(records.exhale?.time || 0).toFixed(1)}s
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200">
                <CardContent className="py-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-violet-800">Apnée Max (BOLT)</p>
                      <p className="text-xs text-violet-600 mt-1">{formatRecordDate(records.hold?.date)}</p>
                    </div>
                    <p className="text-2xl font-bold text-violet-600" data-testid="record-hold">
                      {(records.hold?.time || 0).toFixed(1)}s
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200">
                <CardContent className="py-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-emerald-800">Apnée</p>
                      <p className="text-xs text-emerald-600 mt-1">{formatRecordDate(records.apnea?.date)}</p>
                    </div>
                    <p className="text-2xl font-bold text-emerald-600" data-testid="record-apnea">
                      {(records.apnea?.time || 0).toFixed(1)}s
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-r from-rose-50 to-pink-50 border-rose-200">
                <CardContent className="py-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-rose-800">MBT (pas)</p>
                      <p className="text-xs text-rose-600 mt-1">{formatRecordDate(records.mbt.date)}</p>
                    </div>
                    <p className="text-2xl font-bold text-rose-600" data-testid="record-mbt">
                      {records.mbt.steps} pas
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="progression">
            {loading ? (
              <div className="text-center py-8 text-stone-400">Chargement...</div>
            ) : challengeScores.length === 0 ? (
              <Card className="bg-white/70 backdrop-blur border-stone-200">
                <CardContent className="py-8 text-center">
                  <TrendingUp className="mx-auto mb-3 text-stone-300" size={40} />
                  <p className="text-stone-500">Aucune donnée de progression</p>
                  <p className="text-sm text-stone-400 mt-1">Complétez des défis pour voir vos graphiques</p>
                </CardContent>
              </Card>
            ) : !isMounted ? (
              <div className="text-center py-8 text-stone-400">Chargement des graphiques...</div>
            ) : (
              <div>
                {renderChallengeChart('inhale', 'Inspiration', 'from-amber-50', 'to-orange-50')}
                {renderChallengeChart('exhale', 'Expiration', 'from-sky-50', 'to-indigo-50')}
                {renderChallengeChart('hold', 'Apnée (BOLT)', 'from-violet-50', 'to-purple-50')}
                {renderChallengeChart('apnea', 'Apnée', 'from-emerald-50', 'to-green-50')}
                {renderChallengeChart('mbt', 'MBT (pas)', 'from-rose-50', 'to-pink-50')}
              </div>
            )}
          </TabsContent>

          <TabsContent value="challenges">
            {loading ? (
              <div className="text-center py-8 text-stone-400">Chargement...</div>
            ) : challengeScores.length === 0 ? (
              <Card className="bg-white/70 backdrop-blur border-stone-200">
                <CardContent className="py-8 text-center">
                  <Clock className="mx-auto mb-3 text-stone-300" size={40} />
                  <p className="text-stone-500">Aucun défi enregistré</p>
                  <p className="text-sm text-stone-400 mt-1">Complétez un défi pour voir votre historique</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {challengeScores.map((score) => {
                  const colors = getChallengeColor(score.challenge_type);
                  return (
                    <Card 
                      key={score.id} 
                      className={`${colors.bg} ${colors.border}`}
                      data-testid={`challenge-score-${score.id}`}
                    >
                      <CardContent className="py-3">
                        <div className="flex justify-between items-center gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className={`font-medium ${colors.text}`}>
                                {getChallengeLabel(score.challenge_type)}
                              </p>
                              {score.is_record && (
                                <span className="text-[10px] bg-yellow-400 text-yellow-900 px-1.5 py-0.5 rounded-full font-bold">
                                  RECORD
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-stone-500 mt-1">{formatDate(score.completed_at)}</p>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <p className={`text-xl font-bold ${colors.text}`}>
                              {score.challenge_type === 'mbt'
                                ? `${score.steps || 0} pas`
                                : `${(score.duration_seconds || 0).toFixed(1)}s`}
                            </p>
                            <button
                              type="button"
                              onClick={() => (editingNoteId === score.id ? cancelEditNote() : startEditNote(score))}
                              className={`p-1.5 rounded-lg transition ${score.notes ? 'text-amber-600 hover:bg-amber-50' : 'text-stone-400 hover:text-stone-700 hover:bg-stone-100'}`}
                              aria-label={score.notes ? "Modifier la note" : "Ajouter une note"}
                              title={score.notes ? "Modifier la note" : "Ajouter une note"}
                              data-testid={`challenge-note-toggle-${score.id}`}
                            >
                              {score.notes ? <StickyNote size={16} /> : <Pencil size={16} />}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteChallenge(score)}
                              className="p-1.5 rounded-lg text-stone-400 hover:text-red-600 hover:bg-red-50 transition"
                              aria-label="Supprimer la mesure"
                              data-testid={`challenge-delete-${score.id}`}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>

                        {editingNoteId === score.id ? (
                          <div className="mt-3">
                            <textarea
                              value={noteDraft}
                              onChange={(e) => setNoteDraft(e.target.value.slice(0, 1000))}
                              rows={3}
                              maxLength={1000}
                              placeholder="État émotionnel, stress, fatigue, contexte…"
                              className="w-full px-3 py-2 rounded-lg border border-stone-300 bg-white text-sm text-stone-700 focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                              data-testid={`challenge-note-textarea-${score.id}`}
                            />
                            <div className="flex items-center justify-between mt-1.5">
                              <span className="text-[10px] text-stone-400">{noteDraft.length}/1000</span>
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={cancelEditNote}
                                  disabled={noteSaving}
                                  className="px-2 py-1 text-xs rounded-full bg-stone-100 text-stone-600 hover:bg-stone-200 flex items-center gap-1"
                                  data-testid={`challenge-note-cancel-${score.id}`}
                                >
                                  <XIcon size={12} /> Annuler
                                </button>
                                <button
                                  type="button"
                                  onClick={() => saveNote(score)}
                                  disabled={noteSaving}
                                  className="px-2 py-1 text-xs rounded-full bg-amber-500 text-white hover:bg-amber-600 flex items-center gap-1 disabled:opacity-50"
                                  data-testid={`challenge-note-save-${score.id}`}
                                >
                                  <Check size={12} /> {noteSaving ? 'Enregistrement…' : 'Enregistrer'}
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          score.notes && (
                            <p className="mt-2 text-xs text-stone-600 bg-white/60 border border-stone-200 rounded-lg px-3 py-2 whitespace-pre-wrap" data-testid={`challenge-note-display-${score.id}`}>
                              <StickyNote size={12} className="inline mr-1 text-amber-500" />
                              {score.notes}
                            </p>
                          )
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="wimhof">
            {loading ? (
              <div className="text-center py-8 text-stone-400">Chargement...</div>
            ) : (() => {
              const wimhofSessions = sessions
                .filter(s => s.pattern_id === 'wim-hof' && Array.isArray(s.retention_scores) && s.retention_scores.length > 0)
                .sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at));

              if (wimhofSessions.length === 0) {
                return (
                  <Card className="bg-white/70 backdrop-blur border-stone-200">
                    <CardContent className="py-8 text-center">
                      <Wind className="mx-auto mb-3 text-stone-300" size={40} />
                      <p className="text-stone-500">Aucune session Wim Hof</p>
                      <p className="text-sm text-stone-400 mt-1">Terminez une pratique Wim Hof pour voir vos temps d&apos;apnée ici</p>
                    </CardContent>
                  </Card>
                );
              }

              // Progression table: best apnea per session (chronological, oldest first)
              const progression = [...wimhofSessions].reverse();

              return (
                <div className="space-y-4">
                  {/* Progression table */}
                  <Card className="bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200">
                    <CardContent className="py-4">
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="text-cyan-600" size={18} />
                        <h3 className="font-semibold text-cyan-800">Progression — meilleure apnée par session</h3>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="wimhof-progression-table">
                          <thead>
                            <tr className="border-b border-cyan-200">
                              <th className="text-left py-2 px-2 font-medium text-cyan-700">Date</th>
                              <th className="text-center py-2 px-2 font-medium text-cyan-700">Cycles</th>
                              <th className="text-right py-2 px-2 font-medium text-cyan-700">Meilleure apnée</th>
                            </tr>
                          </thead>
                          <tbody>
                            {progression.map((s, idx) => {
                              const best = s.best_retention || Math.max(...s.retention_scores);
                              const prevBest = idx > 0
                                ? (progression[idx - 1].best_retention || Math.max(...progression[idx - 1].retention_scores))
                                : null;
                              const delta = prevBest !== null ? best - prevBest : null;
                              return (
                                <tr key={s.id} className="border-b border-cyan-100/60">
                                  <td className="py-2 px-2 text-stone-700">{formatDate(s.completed_at)}</td>
                                  <td className="py-2 px-2 text-center text-stone-600">{s.retention_scores.length}</td>
                                  <td className="py-2 px-2 text-right">
                                    <span className="font-bold text-cyan-700">{best}s</span>
                                    {delta !== null && delta !== 0 && (
                                      <span className={`ml-2 text-[10px] ${delta > 0 ? 'text-emerald-600' : 'text-rose-500'}`}>
                                        {delta > 0 ? '+' : ''}{delta}s
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Per-session detail (most recent first) */}
                  <p className="text-xs text-stone-500 uppercase tracking-wide font-medium px-1">Détail des sessions</p>
                  {wimhofSessions.map((s) => {
                    const best = s.best_retention || Math.max(...s.retention_scores);
                    return (
                      <Card key={s.id} className="bg-white/70 backdrop-blur border-stone-200" data-testid={`wimhof-session-${s.id}`}>
                        <CardContent className="py-4">
                          <div className="flex justify-between items-center mb-3">
                            <div>
                              <p className="font-medium text-stone-700">Session Wim Hof</p>
                              <p className="text-xs text-stone-400">{formatDate(s.completed_at)}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-bold text-cyan-600">{best}s</p>
                              <p className="text-[10px] text-stone-400">meilleure apnée</p>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {s.retention_scores.map((r, i) => (
                              <div
                                key={i}
                                className={`px-2 py-1 rounded-md text-xs font-medium ${r === best ? 'bg-cyan-500 text-white' : 'bg-cyan-100 text-cyan-800'}`}
                              >
                                <span className="opacity-70 mr-1">C{i + 1}</span>{r}s
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              );
            })()}
          </TabsContent>

          <TabsContent value="sessions">
            {loading ? (
              <div className="text-center py-8 text-stone-400">Chargement...</div>
            ) : sessions.length === 0 ? (
              <Card className="bg-white/70 backdrop-blur border-stone-200">
                <CardContent className="py-8 text-center">
                  <Wind className="mx-auto mb-3 text-stone-300" size={40} />
                  <p className="text-stone-500">Aucune session</p>
                  <p className="text-sm text-stone-400 mt-1">Complétez une session pour la voir ici</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {sessions.map((session) => (
                  <Card key={session.id} className="bg-white/70 backdrop-blur border-stone-200" data-testid={`session-${session.id}`}>
                    <CardContent className="py-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-stone-700">{session.pattern_name}</p>
                          <p className="text-xs text-stone-400 mt-1">{formatDate(session.completed_at)}</p>
                          {session.air_hunger_rating && (
                            <div className="mt-2 flex items-center gap-1" title={`Soif d'air ressentie : ${session.air_hunger_rating}/5`} data-testid={`session-rating-${session.id}`}>
                              {[1, 2, 3, 4, 5].map((n) => (
                                <span key={n} className={n <= session.air_hunger_rating ? 'text-amber-400' : 'text-stone-300'} style={{ fontSize: '13px', lineHeight: '13px' }}>★</span>
                              ))}
                              <span className="text-[10px] text-stone-500 ml-1">soif d&apos;air</span>
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-teal-600">{(session.duration_minutes || 0).toFixed(1)} min</p>
                          <p className="text-xs text-stone-400">{session.cycles_completed} cycles</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default HistoryPage;
